"use client";

import { Camera, Lock, X, Zap } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { SPOT_TYPE_LABELS } from "~/lib/constants/spot-type";
import { cn } from "~/lib/utils";
import type { SearchSpotType } from "~/types/search";
const SPOT_TYPES: Array<{
  value: SearchSpotType | null;
  label: string;
}> = [{
  value: null,
  label: "Any"
}, {
  value: "indoor",
  label: SPOT_TYPE_LABELS.indoor
}, {
  value: "outdoor",
  label: SPOT_TYPE_LABELS.outdoor
}, {
  value: "covered",
  label: SPOT_TYPE_LABELS.covered
}, {
  value: "garage",
  label: SPOT_TYPE_LABELS.garage
}];
interface FiltersSheetProps {
  open: boolean;
  onClose: () => void;
}
export function getActiveFilterCount(params: URLSearchParams): number {
  let count = 0;
  if (params.get("spot_type")) count++;
  if (params.get("ev_charger") === "true") count++;
  if (params.get("security_camera") === "true") count++;
  if (params.get("gated") === "true") count++;
  return count;
}
export function FiltersSheet({
  open,
  onClose
}: FiltersSheetProps) {
  const rawParams = useSearchParams();
  const [spotType, setSpotType] = useState<SearchSpotType | null>(rawParams.get("spot_type") as SearchSpotType | null ?? null);
  const [evCharger, setEvCharger] = useState(rawParams.get("ev_charger") === "true");
  const [securityCamera, setSecurityCamera] = useState(rawParams.get("security_camera") === "true");
  const [gated, setGated] = useState(rawParams.get("gated") === "true");

  // Sync draft state when URL changes or sheet opens
  useEffect(() => {
    if (open) {
      setSpotType(rawParams.get("spot_type") as SearchSpotType | null ?? null);
      setEvCharger(rawParams.get("ev_charger") === "true");
      setSecurityCamera(rawParams.get("security_camera") === "true");
      setGated(rawParams.get("gated") === "true");
    }
  }, [open, rawParams]);
  if (!open) return null;
  const handleApply = () => {
    // Live, not `useSearchParams`: this preserves the window and destination from
    // whatever base it reads, and that hook lags a `pushState` by the router's
    // transition — so a stale base here would write back the time the user just
    // changed. The Filters button commits the pill before opening this sheet.
    const next = new URLSearchParams(window.location.search);
    if (spotType) next.set("spot_type", spotType);else next.delete("spot_type");
    if (evCharger) next.set("ev_charger", "true");else next.delete("ev_charger");
    if (securityCamera) next.set("security_camera", "true");else next.delete("security_camera");
    if (gated) next.set("gated", "true");else next.delete("gated");
    // Replace, not push: filters narrow the destination already on screen rather than
    // moving to a new one, and only a new destination earns a history entry.
    window.history.replaceState(null, "", `/search?${next.toString()}`);
    onClose();
  };
  const handleClear = () => {
    setSpotType(null);
    setEvCharger(false);
    setSecurityCamera(false);
    setGated(false);
  };
  const AMENITIES = [{
    key: "ev",
    label: "EV Charging",
    icon: Zap,
    value: evCharger,
    toggle: () => setEvCharger(v => !v)
  }, {
    key: "gated",
    label: "Gated",
    icon: Lock,
    value: gated,
    toggle: () => setGated(v_0 => !v_0)
  }, {
    key: "camera",
    label: "Camera",
    icon: Camera,
    value: securityCamera,
    toggle: () => setSecurityCamera(v_1 => !v_1)
  }] as const;
  return <div className="fixed inset-0 z-[100] flex items-end justify-center md:items-center">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/40 cursor-default" onClick={onClose} />
      <div className="relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-2xl bg-white md:w-[480px] md:rounded-2xl md:max-h-[85dvh]">
        {/* Drag handle (mobile only) */}
        <div className="mx-auto mt-3 mb-1 h-1 w-10 shrink-0 rounded-full bg-[var(--ink-20)] md:hidden" />

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--ink-10)] px-5 py-3 shrink-0">
          <span className="text-[16px] font-semibold text-[var(--ink)]">
            Filters
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={handleClear} className="px-2 py-1 text-[13px] font-semibold text-[var(--ink-60)] underline transition-colors hover:text-[var(--ink)]">
              Clear all
            </button>
            <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[var(--ink-05)]" aria-label="Close filters">
              <X className="h-4 w-4 text-[var(--ink)]" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Spot type */}
          <section className="px-5 py-5">
            <h3 className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-60)]">
              Spot type
            </h3>
            <div className="flex flex-wrap gap-2">
              {SPOT_TYPES.map(({
              value,
              label
            }) => <button key={label} type="button" onClick={() => setSpotType(value)} className={cn("h-9 rounded-full border px-4 text-[13px] font-medium transition-all", spotType === value ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--ink-20)] bg-white text-[var(--ink-60)] hover:border-[var(--ink-40)] hover:text-[var(--ink)]")}>
                  {label}
                </button>)}
            </div>
          </section>

          <div className="mx-5 border-t border-[var(--ink-10)]" />

          {/* Amenities */}
          <section className="px-5 py-5">
            <h3 className="mb-3 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-60)]">
              Amenities
            </h3>
            <div className="space-y-3">
              {AMENITIES.map(({
              key,
              label: label_0,
              icon: Icon,
              value: value_0,
              toggle
            }) => <button key={key} type="button" onClick={toggle} className={cn("flex w-full items-center justify-between rounded-xl border px-4 py-3 transition-all", value_0 ? "border-[var(--blue)] bg-[var(--blue-pale)] text-[var(--blue)]" : "border-[var(--ink-20)] bg-white text-[var(--ink)] hover:border-[var(--ink-40)]")}>
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="text-[14px] font-medium">{label_0}</span>
                  </div>
                  {/* Toggle indicator */}
                  <div className={cn("h-5 w-9 rounded-full transition-colors relative", value_0 ? "bg-[var(--blue)]" : "bg-[var(--ink-20)]")}>
                    <div className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform", value_0 ? "translate-x-4" : "translate-x-0.5")} />
                  </div>
                </button>)}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[var(--ink-10)] px-5 py-4 shrink-0">
          <span className="text-[13px] text-[var(--ink-60)]">
            {[spotType && "Spot type", evCharger && "EV Charging", securityCamera && "Camera", gated && "Gated"].filter(Boolean).join(", ") || "No filters applied"}
          </span>
          <Button type="button" variant="primary" size="md" onClick={handleApply}>
            Show results
          </Button>
        </div>
      </div>
    </div>;
}